import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Cart rewards ladder — free delivery + two cumulative gift tiers.
 *
 * Two elements, both fed by server-rendered state. `cart-rewards.liquid`
 * recomputes the ladder on every cart morph and re-stamps the attributes below,
 * so inside the cart neither element derives progress itself:
 *
 *   <cart-rewards>         the progress bar. Presentation only — it just plays a
 *                          one-shot celebration when the unlocked count rises.
 *
 *   <cart-rewards-gifts>   the gift choosers. Adds / swaps / removes the gift
 *                          lines to match the customer's picks and the tiers
 *                          they currently qualify for.
 *
 * LIVE MODE: on surfaces outside the cart section — the product page USP — no
 * morph ever re-renders the bar, so `data-live` hands <cart-rewards> the tier
 * thresholds and copy and it recomputes the same state client-side on every cart
 * update. The initial paint is still server-rendered from the live `cart`, so
 * there is no flash. This mirrors the Liquid maths exactly; keep the two in step.
 *
 * Gift lines carry a hidden `_gift: 'true'` property — a merchant-configured
 * Shopify automatic discount (100% off any `_gift` line) is what makes them
 * free; this code never prices anything. A second hidden `_gift_tier` property
 * ('Step 1' / 'Step 2') keeps the two tiers independently addressable, so tier
 * 1's pick can be swapped without disturbing tier 2, and lets the merchant's
 * Shopify Flow tell the two gifts apart when tagging orders. Lines predating the
 * two-tier ladder have no `_gift_tier` and are treated as tier 1.
 */
const GIFT_PROPERTY = '_gift';
const TIER_PROPERTY = '_gift_tier';

/**
 * Threshold, in cents, that earned this gift line, stamped on at the moment it
 * is added.
 *
 * THIS IS A CONTRACT WITH THE CHECKOUT EXTENSION (`gwp-checkout-guard` in the
 * Buff Browz custom app). Theme JS only runs in the cart, so a discount code
 * entered on the checkout page can drop the payable total under a threshold
 * with nothing here to react to it. The extension re-runs the same check at
 * checkout and removes the line; carrying the threshold on the line itself is
 * what stops the extension needing its own copy of the theme's settings.
 *
 * Lines added before this property existed simply carry no `_gift_min`, and the
 * extension leaves those alone rather than guessing.
 */
const MIN_PROPERTY = '_gift_min';

/**
 * Value written to `_gift_tier`, per tier.
 *
 * THIS IS A CONTRACT WITH SHOPIFY FLOW. The merchant's flow reads this line
 * property off the order and tags it "GWP: Step 1" / "GWP: Step 2", so the
 * strings must stay in step with the flow's conditions — changing them silently
 * stops orders being tagged. It is deliberately the human-readable label rather
 * than a bare number so the flow condition reads the same as the tag it writes.
 *
 * `_gift` itself is a separate contract: the Shopify discount function matches
 * `_gift IS true` exactly, which is what prices the gift at £0. Never fold the
 * tier into that property.
 */
const TIER_LABEL = { 1: 'Step 1', 2: 'Step 2' };
const TIERS = [1, 2];

/**
 * Qualifying spend = what the customer actually pays, minus the gift lines.
 * Mirrors `qualifying_cents` in cart-rewards.liquid.
 * @param {Object | null} cart
 * @returns {number | null}
 */
function qualifyingSpend(cart) {
  if (typeof cart?.total_price !== 'number') return null;
  const giftCents = (cart.items ?? [])
    .filter((item) => item.properties?.[GIFT_PROPERTY])
    .reduce((sum, item) => sum + (item.final_line_price ?? 0), 0);
  return cart.total_price - giftCents;
}

class CartRewards extends HTMLElement {
  /** @type {MutationObserver | null} */
  #observer = null;

  /** Last known number of banked milestones, to fire the celebration only on a rise. */
  #unlockedCount = 0;

  /** @type {AbortController | null} */
  #inFlight = null;

  /** @type {Array<{kind: string, cents: number, copy: string}>} */
  #tiers = [];

  get #isLive() {
    return this.hasAttribute('data-live');
  }

  connectedCallback() {
    this.#unlockedCount = Number(this.dataset.unlockedCount || 0);

    // The cart section morphs in two ways: attributes patched in place (observer
    // fires) or the element recreated (connectedCallback fires). Cover both.
    this.#observer = new MutationObserver(() => {
      const count = Number(this.dataset.unlockedCount || 0);
      if (count > this.#unlockedCount) this.#celebrate();
      this.#unlockedCount = count;
    });

    this.#observer.observe(this, { attributes: true, attributeFilter: ['data-unlocked-count'] });

    if (this.#isLive) {
      try {
        this.#tiers = JSON.parse(this.dataset.tiers || '[]');
      } catch {
        this.#tiers = [];
      }
      document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
      // Re-sync once on connect in case the page HTML was served from cache.
      this.#syncFromCart();
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#inFlight?.abort();
  }

  #onCartUpdate = async () => {
    // The event's payload doesn't always carry line-item properties, and we need
    // those to net off the gift lines — so always read the authoritative cart.
    this.#syncFromCart();
  };

  async #syncFromCart() {
    this.#inFlight?.abort();
    this.#inFlight = new AbortController();

    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const res = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        signal: this.#inFlight.signal,
      });
      if (!res.ok) return;
      const spend = qualifyingSpend(await res.json());
      if (typeof spend === 'number') this.#render(spend);
    } catch {
      /* aborted or offline — leave the server-rendered state alone */
    }
  }

  /**
   * Recompute the ladder for a qualifying spend and restate the DOM. Mirrors the
   * `progress` / `unlocked_count` / stop-state maths in cart-rewards.liquid.
   * @param {number} spend
   */
  #render(spend) {
    if (!this.#tiers.length) return;

    const count = this.#tiers.length;
    const span = 100 / count;

    let unlocked = 0;
    let lowerCents = 0;
    let next = null;
    for (const tier of this.#tiers) {
      if (tier.cents > spend) {
        if (!next) next = tier;
      } else {
        unlocked += 1;
        lowerCents = tier.cents;
      }
    }

    let progress = 100;
    if (next) {
      const spanCents = next.cents - lowerCents;
      const fraction = spanCents > 0 ? Math.max(0, (spend - lowerCents) / spanCents) : 0;
      progress = Math.round((unlocked * span + fraction * span) * 100) / 100;
    }

    this.style.setProperty('--rewards-progress', `${progress}%`);
    this.toggleAttribute('data-all-unlocked', !next);

    for (const stop of this.querySelectorAll('.rewards__stop')) {
      const cents = Number(stop.dataset.cents || 0);
      const state = cents <= spend ? 'unlocked' : stop.dataset.kind === next?.kind ? 'next' : 'locked';
      stop.dataset.state = state;
    }

    const headlineEl = this.querySelector('.rewards__headline');
    if (headlineEl) {
      // formatMoney dereferences both, so never hand it an undefined.
      const format = this.dataset.moneyFormat || '{{amount}}';
      const currency = this.dataset.currency || 'GBP';
      const remaining = next ? formatMoney(next.cents - spend, format, currency) : '';
      const copy = next ? next.copy : this.dataset.successCopy || '';
      // Copy comes from merchant settings and may be inline richtext, exactly as
      // the Liquid renders it — so mirror that rather than flattening to text.
      headlineEl.innerHTML = copy.replaceAll('[amount]', remaining);
    }

    // Setting the attribute last lets the existing observer fire the celebration.
    this.dataset.unlockedCount = String(unlocked);
  }

  #celebrate() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.classList.remove('is-celebrating');
    // Force reflow so the animation restarts if it was mid-flight.
    void this.offsetWidth;
    this.classList.add('is-celebrating');

    this.addEventListener('animationend', () => this.classList.remove('is-celebrating'), { once: true });
  }
}

class CartRewardsGifts extends HTMLElement {
  /** Serialises mutations across every instance (drawer + cart page). */
  static inFlight = false;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {MutationObserver | null} */
  #subtreeObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#observer = new MutationObserver(() => this.#reconcile());
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-tier1-unlocked', 'data-tier2-unlocked', 'data-tier1-variant', 'data-tier2-variant'],
    });

    // Delegated on the host so they survive the choosers being re-rendered on
    // morph. `scroll` doesn't bubble, so it's caught in the capture phase.
    this.addEventListener('change', this.#onChoice);
    this.addEventListener('click', this.#onArrowClick);
    this.addEventListener('scroll', this.#syncArrows, true);
    window.addEventListener('resize', this.#syncArrows);

    // Morph swaps the tiles out without touching the host's own attributes, so
    // watch the subtree too or the arrows keep a stale enabled/hidden state.
    // (childList/subtree only — toggling the buttons' own attributes must not
    // re-enter this.)
    this.#subtreeObserver = new MutationObserver(this.#syncArrows);
    this.#subtreeObserver.observe(this, { childList: true, subtree: true });

    // Tile widths settle once the gift images have loaded.
    this.#resizeObserver = new ResizeObserver(this.#syncArrows);
    for (const row of this.#rows) this.#resizeObserver.observe(row);

    this.#syncArrows();
    this.#reconcile();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#subtreeObserver?.disconnect();
    this.#subtreeObserver = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.removeEventListener('change', this.#onChoice);
    this.removeEventListener('click', this.#onArrowClick);
    this.removeEventListener('scroll', this.#syncArrows, true);
    window.removeEventListener('resize', this.#syncArrows);
  }

  /** @returns {HTMLElement[]} the scrolling tile rows, one per gift tier */
  get #rows() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.reward-gift__choices')));
  }

  /**
   * Show the arrows only where the row actually overflows, and disable whichever
   * end it's already at. Without this a mouse-only visitor has no scrollbar (it's
   * hidden), no dependable wheel gesture, and no hint that more gifts exist.
   */
  #syncArrows = () => {
    for (const row of this.#rows) {
      const scroller = row.parentElement;
      if (!scroller) continue;

      const overflow = row.scrollWidth - row.clientWidth;
      const overflows = overflow > 1;
      // Round: sub-pixel widths otherwise leave "next" enabled at the far end.
      const left = Math.round(row.scrollLeft);

      for (const button of scroller.querySelectorAll('[data-gift-scroll]')) {
        const forward = Number(/** @type {HTMLElement} */ (button).dataset.giftScroll) > 0;
        /** @type {HTMLButtonElement} */ (button).hidden = !overflows;
        /** @type {HTMLButtonElement} */ (button).disabled = forward ? left >= Math.round(overflow) : left <= 0;
      }
    }
  };

  /** Scroll by whole tiles so the row lands on a snap point. */
  #onArrowClick = (event) => {
    const button = /** @type {HTMLElement | null} */ (event.target?.closest?.('[data-gift-scroll]'));
    if (!button) return;

    const row = button.parentElement?.querySelector('.reward-gift__choices');
    if (!row) return;

    const tile = row.querySelector('.reward-gift__choice');
    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;
    const stride = tile ? tile.getBoundingClientRect().width + gap : row.clientWidth;
    // A whole number of tiles, at least one, never more than a screenful.
    const tiles = Math.max(1, Math.floor(row.clientWidth / stride));

    row.scrollBy({
      left: stride * tiles * Number(button.dataset.giftScroll),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  };

  get #root() {
    return window.Shopify?.routes?.root || '/';
  }

  /** @param {number} tier */
  #isUnlocked(tier) {
    return this.hasAttribute(`data-tier${tier}-unlocked`);
  }

  /** Variant id of the gift currently in the cart for this tier (0 = none). */
  #chosenVariant(tier) {
    return Number(this.dataset[`tier${tier}Variant`] || 0);
  }

  /** This tier's threshold in cents, server-rendered (0 = unknown). */
  #tierCents(tier) {
    return Number(this.dataset[`tier${tier}Cents`] || 0);
  }

  /** Variant ids this tier currently offers (in-stock, server-rendered). */
  #offeredVariants(tier) {
    const inputs = this.querySelectorAll(`[data-gift-choice][data-gift-tier="${tier}"]`);
    return new Set(Array.from(inputs, (input) => Number(/** @type {HTMLInputElement} */ (input).value)));
  }

  /** The customer picked a gift. */
  #onChoice = (event) => {
    const input = /** @type {HTMLInputElement | null} */ (event.target?.closest?.('[data-gift-choice]'));
    if (!input || !input.checked || input.disabled) return;

    const tier = Number(input.dataset.giftTier || 1);
    if (!this.#isUnlocked(tier)) return;

    const track = /** @type {any} */ (window).track;
    if (typeof track === 'function') {
      track('buff_gift_with_purchase_select', {
        product_name: input.dataset.giftTitle,
        product_id: input.dataset.giftProductId,
        variant_id: input.value,
        gift_tier: tier,
      });
    }

    this.#selectGift(tier, Number(input.value));
  };

  /**
   * Keep the cart in sync with the server-rendered state. Only ever *removes*:
   * a tier that has re-locked, or a gift that is no longer on offer (sold out /
   * dropped from the merchant's list). Adding is exclusively #onChoice's job, so
   * no gift ever appears in the cart the customer didn't ask for.
   */
  async #reconcile() {
    if (CartRewardsGifts.inFlight) return;

    // Decide from the server-rendered attributes first so the common case (a
    // morph that changed nothing relevant) costs no network at all.
    const stale = TIERS.filter((tier) => {
      const variant = this.#chosenVariant(tier);
      if (!variant) return false;
      return !this.#isUnlocked(tier) || !this.#offeredVariants(tier).has(variant);
    });
    if (stale.length === 0) return;

    CartRewardsGifts.inFlight = true;
    try {
      let cart = await this.#getCart();
      let changed = false;

      for (const tier of stale) {
        const line = this.#findGiftLine(cart, tier);
        if (!line) continue;

        const updated = await this.#changeLine(line.key, 0);
        if (updated) {
          cart = updated;
          changed = true;
        }
      }

      if (changed) this.#announceUpdate(cart);
    } catch (error) {
      console.error('[cart-rewards]', error);
    } finally {
      CartRewardsGifts.inFlight = false;
    }
  }

  /**
   * Add this tier's chosen gift, replacing a different pick for the same tier.
   * The other tier's gift is left alone — the tiers are cumulative.
   *
   * @param {number} tier
   * @param {number} variantId
   */
  async #selectGift(tier, variantId) {
    if (CartRewardsGifts.inFlight || !variantId) return;
    CartRewardsGifts.inFlight = true;
    try {
      const existing = this.#findGiftLine(await this.#getCart(), tier);
      if (existing?.variant_id === variantId) return; // already selected

      // Swap: drop the previous pick first so a tier only ever holds one gift.
      if (existing && !(await this.#changeLine(existing.key, 0))) return;

      const res = await fetch(`${this.#root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: {
                [GIFT_PROPERTY]: 'true',
                [TIER_PROPERTY]: TIER_LABEL[tier],
                ...(this.#tierCents(tier) > 0 ? { [MIN_PROPERTY]: String(this.#tierCents(tier)) } : {}),
              },
            },
          ],
        }),
      });

      if (!res.ok) {
        // Add failed (e.g. the gift went out of stock between render and click).
        // Re-sync so the UI reflects the cart's true state — including the
        // previous pick we already removed.
        console.warn('[cart-rewards] could not add gift', res.status);
      }

      this.#announceUpdate(await this.#getCart());
    } catch (error) {
      console.error('[cart-rewards]', error);
    } finally {
      CartRewardsGifts.inFlight = false;
    }
  }

  /**
   * Change a line's quantity.
   *
   * @param {string} key
   * @param {number} quantity
   * @returns {Promise<Object | null>} the updated cart, or null on failure
   */
  async #changeLine(key, quantity) {
    const res = await fetch(`${this.#root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    return res.ok ? res.json() : null;
  }

  /**
   * The gift line belonging to a tier. Lines written before the two-tier ladder
   * carry no `_gift_tier`, so an untagged gift counts as tier 1. Bare "1"/"2"
   * values are also accepted: carts created between the two-tier build and the
   * switch to labels are still live in shoppers' browsers.
   *
   * @param {Object | null} cart
   * @param {number} tier
   */
  #findGiftLine(cart, tier) {
    return (
      cart?.items?.find((item) => {
        if (!item.properties?.[GIFT_PROPERTY]) return false;
        const value = item.properties[TIER_PROPERTY];
        if (!value) return tier === 1;
        return value === TIER_LABEL[tier] || value === String(tier);
      }) ?? null
    );
  }

  /** Tell the theme to re-render the cart sections. */
  #announceUpdate(cart) {
    document.dispatchEvent(
      new CartUpdateEvent(cart ?? {}, 'cart-rewards', {
        source: 'cart-rewards',
        itemCount: cart?.item_count,
      })
    );
  }

  async #getCart() {
    try {
      const res = await fetch(`${this.#root}cart.js`, { headers: { Accept: 'application/json' } });
      return res.ok ? await res.json() : null;
    } catch {
      return null;
    }
  }
}

if (!customElements.get('cart-rewards')) {
  customElements.define('cart-rewards', CartRewards);
}

if (!customElements.get('cart-rewards-gifts')) {
  customElements.define('cart-rewards-gifts', CartRewardsGifts);
}
